import React from "react"
import nl2br from "../utils/nl2br"
import { cva, cx } from "class-variance-authority"

export const eyebrowClasses = cva(
  "uppercase tracking-wide leading-tight font-bold block",
  {
    variants: {
      color: {
        white: "text-white",
        cyan: "text-cyan",
        cyanLight: "text-cyan-light",
        gray: "text-gray-700",
      },
      size: {
        xs: "text-xs",
        sm: "text-sm",
      },
    },
    defaultVariants: {
      color: "gray",
      size: "sm",
    },
  }
)

export interface EyebrowProps {
  /**
   * optional class added to the outermost div
   */
  as?: React.ElementType | keyof JSX.IntrinsicElements
  /**
   * content
   */
  children: React.ReactNode
  /**
   * text color. "gray" unless value is "cyan", "light cyan", or "white"
   */
  color?: "cyan" | "gray" | "white" | "cyanLight"
  /**
   * text size. "sm" unless value is "xs"
   */
  size?: "xs" | "sm"
  /**
   * optional additional classes
   */
  className?: string
}

export const Eyebrow = ({
  as = "p",
  children,
  color = "gray",
  size = "sm",
  className,
  ...rest
}: EyebrowProps) => {
  const Component = as

  return (
    <Component className={eyebrowClasses({ color, size, className })} {...rest}>
      {children}
    </Component>
  )
}

const headingClasses = cva("font-bold", {
  variants: {
    color: {
      gray: "text-gray-700",
      orange: "text-orange",
      teal: "text-cyan",
      cyan: "text-cyan",
      black: "text-gray-900",
      white: "text-white",
    },
    width: {
      full: "md:w-full",
      "4/5": "md:w-4/5",
      "3/4": "md:w-3/4",
      "2/3": "md:w-2/3",
      "1/2": "md:w-2/3 lg:w-1/2",
    },
    size: {
      base: "text-base leading-normal",
      "3xl": "text-3xl leading-tight",
      "2xl": "text-2xl leading-tight",
      xl: "text-xl leading-tight",
      lg: "text-lg leading-snug",
    },
    textAlign: {
      inherit: "",
      center: "text-center",
    },
    transform: {
      default: "",
      uppercase: "uppercase",
    },
    includeDivider: {
      true: "pb-4 border-b border-gray-300 prose-heading-divider",
    },
  },
  defaultVariants: {
    color: "black",
    size: "base",
  },
})

export interface HeadingProps {
  /**
   * The element the heading should render as
   */
  headingLevel?: React.ElementType | keyof JSX.IntrinsicElements
  /**
   * An option to include a divider underneath the heading
   */
  includeDivider?: boolean
  /**
   * The size of the heading
   */
  size?: "base" | "3xl" | "2xl" | "xl" | "lg"
  /**
   * Additional classes to add to the heading element
   */
  className?: string
  /**
   * The color of the heading
   */
  color?: "gray" | "cyan" | "orange" | "teal" | "black" | "white"
  /**
   * The text alignment of the heading
   */
  transform?: "default" | "uppercase"
  /**
   * The text to be displayed in the heading
   */
  text: string
  /**
   * The text of the eyebrow to be included with the heading
   */
  eyebrow?: React.ReactNode
  /**
   * The color of the eyebrow
   */
  eyebrowColor?: EyebrowProps["color"]
  /**
   * The size of the eyebrow
   */
  eyebrowSize?: EyebrowProps["size"]
  /**
   * The text alignment of the heading
   */
  textAlign?: "inherit" | "center"
  /**
   * The width of the heading container
   */
  width?: "full" | "4/5" | "3/4" | "2/3" | "1/2"
}

/**
 * A heading element with an optional eyebrow.
 */
const Heading = ({
  headingLevel,
  className,
  includeDivider,
  size = "base",
  color,
  transform,
  text,
  eyebrow,
  eyebrowColor,
  eyebrowSize,
  textAlign,
  width,
}: HeadingProps) => {
  /* TODO: Add more flexible eyebrow colors — previously defaulted to teal */
  const Component = headingLevel ?? "h2"

  return (
    <Component
      className={cx(
        headingClasses({
          className,
          color,
          includeDivider,
          size,
          textAlign,
          transform,
          width,
        }),
        textAlign === "center" && width ? `mx-auto` : ""
      )}
      as={headingLevel}
    >
      {eyebrow ? (
        <Eyebrow
          as="span"
          color={eyebrowColor}
          size={eyebrowSize}
          className={size === "2xl" ? `mb-[0.5em]` : `mb-[0.333em]`}
        >
          {eyebrow}
        </Eyebrow>
      ) : null}
      {nl2br(text)}
    </Component>
  )
}

export default Heading
